import { type Discount, DiscountType } from '@carvana/merch-contracts';
import {
  defaultLoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useContext, useMemo } from 'react';
import { DeepPartial } from '~/types';

export const defaultContext = {
  discounts: defaultLoadableState<Discount[]>([]),
  hasDiscount: false,
  isInExcludedState: false,
  totalDiscount: 0,
  totalSiteWideDiscount: 0,
  tradeIncentive: 0
};

export type ContextType = typeof defaultContext;

const DiscountContext = createContext<ContextType>(defaultContext);

type OwnProps = React.PropsWithChildren<{
  hasActiveTrade: boolean;
  isTradeInApplied: boolean;
}>;

export function DiscountContextProvider({
  children,
  hasActiveTrade,
  isTradeInApplied
}: OwnProps) {
  const discounts = useLoadableState<Discount[]>([]);

  const value = useMemo<ContextType>(() => {
    const {
      isInExcludedState,
      totalSiteWideDiscount,
      totalDiscount,
      tradeIncentive
    } = discounts.data.reduce(
      (acc, discount) => {
        if (discount.shouldApplyToStickerPrice) {
          acc.totalDiscount += discount.amount;

          if (discount.type === DiscountType.TradeIncentive) {
            const shouldApplyTradeIncentive =
              hasActiveTrade && isTradeInApplied;

            if (hasActiveTrade) {
              acc.tradeIncentive = discount.amount;
            }

            if (!shouldApplyTradeIncentive) {
              // We want the discount amount for advertising purposes, but don't want it to apply to the total discount
              acc.totalDiscount -= discount.amount;
            }
          }
        } else {
          acc.isInExcludedState = true;
        }
        if (
          discount.type === DiscountType.Campaign ||
          discount.type === DiscountType.CyberMondayDeal
        ) {
          acc.totalSiteWideDiscount += discount.amount;
        }

        return acc;
      },
      {
        isInExcludedState: false,
        totalSiteWideDiscount: 0,
        totalDiscount: 0,
        tradeIncentive: 0
      }
    );

    return {
      discounts,
      hasDiscount: !isInExcludedState && totalDiscount > 0,
      isInExcludedState,
      totalDiscount,
      totalSiteWideDiscount,
      tradeIncentive
    };
  }, [discounts, hasActiveTrade, isTradeInApplied]);

  return (
    <DiscountContext.Provider value={value}>
      {children}
    </DiscountContext.Provider>
  );
}

DiscountContextProvider.displayName = 'DiscountContextProvider';

export const generateDiscountContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useDiscounts() {
  return useContext(DiscountContext).discounts.data;
}

export function useIsDiscountsLoaded() {
  return useContext(DiscountContext).discounts.isLoaded;
}

export default DiscountContext;
